import { useQuery } from '@tanstack/react-query';
import { whatsappFlowsApi, WhatsAppFlowRecord } from '@/lib/api/services/whatsappFlows';
import { ApiError } from '@/lib/api';

// Hook to fetch WhatsApp flows from local database (synced from Meta)
export function useWhatsAppFlows(businessAccountId?: string) {
  return useQuery({
    queryKey: ['whatsapp-flows', businessAccountId],
    queryFn: async () => {
      if (!businessAccountId) return [];

      const response = await whatsappFlowsApi.list(businessAccountId, false);

      if (response.error) {
        throw ApiError.fromResult(response);
      }

      // Extract flows from response. Return the RAW list here — filtering lives
      // in `select` below. The query key ['whatsapp-flows', baId] is SHARED with
      // the WhatsApp Forms page (WhatsAppFlows.tsx), which fetches the full
      // unfiltered list. If we filtered inside queryFn, whichever query
      // populated the shared cache would win: when the Forms page ran first, its
      // unfiltered data was served here verbatim and drafts leaked into the
      // automation node dropdown. `select` runs per-consumer on the cached data,
      // so this hook always sees published-only regardless of who filled the cache.
      let flows: WhatsAppFlowRecord[] = [];

      if (Array.isArray(response.data)) {
        flows = response.data;
      } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        flows = Array.isArray(response.data.data) ? response.data.data : [];
      }

      return flows;
    },
    // Per-consumer transform — published/approved only. Does NOT mutate the
    // shared cache, so the Forms page still sees the full list.
    select: (flows: WhatsAppFlowRecord[]) =>
      (Array.isArray(flows) ? flows : []).filter(flow =>
        flow.status?.toLowerCase() === 'published' || flow.status?.toLowerCase() === 'approved'
      ),
    enabled: !!businessAccountId,
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: false,
    retry: 2
  });
}

